import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  IconButtonProps,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

interface SubHeaderItem {
  icon: string;
  label: string;
  value: string;
  // Icon-box background matching the icon's stroke color (Figma). Items
  // without one fall back to the ICON_TINTS cycle below.
  tint?: string;
}

interface SubHeaderProps {
  isOpen: boolean;
  onClose: () => void;
  items: SubHeaderItem[];
  // "popup": floating overlay card anchored below the navbar (vertical
  // layout) — does not affect the page content height. "inline" (default):
  // classic full-width bar used by the horizontal layout.
  variant?: 'inline' | 'popup';
}

// Figma: each detail chip's icon sits in a small tinted rounded box; the tint
// cycles blue → green → purple per item.
const ICON_TINTS = [
  'rgba(60, 119, 149, 0.12)',
  'rgba(17, 197, 17, 0.12)',
  'rgba(156, 39, 176, 0.12)',
];

const ScrollButton = (props: IconButtonProps & { direction: 'left' | 'right' }) => (
  <IconButton
    {...props}
    size="small"
    sx={{
      zIndex: 2,
      bgcolor: 'background.paper',
      border: 1,
      borderColor: 'divider',
      borderRadius: '4px',
      width: 24,
      height: 24,
      '&:hover': {
        bgcolor: 'action.hover',
      },
      '& .MuiSvgIcon-root': {
        fontSize: 16,
      },
    }}
  >
    {props.direction === 'left' ? <KeyboardArrowLeftIcon /> : <KeyboardArrowRightIcon />}
  </IconButton>
);

const SubHeader: React.FC<SubHeaderProps> = ({ isOpen, onClose, items, variant = 'inline' }) => {
  const mode = useSelector((state: RootState) => state.theme.mode);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [showLeftScroll, setShowLeftScroll] = React.useState(false);
  const [showRightScroll, setShowRightScroll] = React.useState(false);

  const checkScroll = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth);
    }
  };

  React.useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [items]);

  const scroll = (direction: 'left' | 'right') => {
    if (containerRef.current) {
      const scrollAmount = 200;
      containerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (!isOpen) return null;

  return (
    <Box
      sx={{
        width: '100%',
        bgcolor: mode === 'dark' ? 'background.paper' : '#ffffff',
        position: 'relative',
        ...(variant === 'popup'
          ? {
              // Floating card attached flush under the navbar: square top
              // corners so it reads as an extension of the navbar card.
              // Figma: 12px bottom radius, 1px top seam, soft #3C7795 shadow.
              borderRadius: '0 0 12px 12px',
              borderTop: '1px solid',
              borderColor: mode === 'dark' ? '#444' : '#DDDDDD',
              boxShadow:
                mode === 'dark'
                  ? '0 2px 8px rgba(0, 0, 0, 0.55)'
                  : '0px 2px 8px 0px rgba(60, 119, 149, 0.1)',
              overflow: 'hidden',
            }
          : {
              borderBottom: 1,
              borderColor: mode === 'dark' ? '#444' : 'divider',
            }),
      }}
    >
      <Box
        sx={{
          // Figma popup: 10px vertical / 24px horizontal padding
          px: 3,
          py: variant === 'popup' ? '10px' : 1,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          position: 'relative',
        }}
      >
        {showLeftScroll && <ScrollButton direction="left" onClick={() => scroll('left')} />}
        
        <Box
          ref={containerRef}
          sx={{
            display: 'flex',
            gap: 3,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            flex: 1,
          }}
          onScroll={checkScroll}
        >
          {items.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                flexShrink: 0,
                // Figma: items separated by whitespace only, no dividers
                mr: index === items.length - 1 ? 0 : 3,
              }}
            >
              <Box
                sx={{
                  // Figma: 28x28 box, 3.5px radius, 16px icon centered
                  width: 28,
                  height: 28,
                  borderRadius: '3.5px',
                  // Per-item tinted icon boxes matching the icon stroke color
                  bgcolor: item.tint || ICON_TINTS[index % ICON_TINTS.length],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <img src={item.icon} alt={item.label} style={{ width: 16, height: 16, objectFit: 'contain' }} />
              </Box>
              <Box>
                <Typography fontSize={10.5} color="text.secondary" sx={{ whiteSpace: 'nowrap', lineHeight: 1.4 }}>
                  {item.label}
                </Typography>
                <Typography fontSize={12.5} fontWeight={400} sx={{ whiteSpace: 'nowrap', lineHeight: 1.4 }}>
                  {item.value}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          {showRightScroll && <ScrollButton direction="right" onClick={() => scroll('right')} />}
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: '50%',
              p: 0.5,
              width: 22,
              height: 22,
              flexShrink: 0,
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default SubHeader;